import { Download, Pool } from '@mui/icons-material'
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    ModalClose,
    ModalDialog,
    Stack,
    Typography,
} from '@mui/joy'
import { useApi } from 'api'
import { useListingFilesApi, useListingType } from 'apiProviders'
import axios from 'axios'
import Modal from 'components/modal'
import { useListingContext } from 'listings/listingProvider'
import { prop } from 'ramda'
import { useState } from 'react'

interface QbccInfo {
    'Local Government Authority Area': string
    'Number of Pools': string
    'Post Code': string | null
    'Shared Pool Property': string
    'Site Name': string
    'Street Name': string
    'Street Number': string
    'Street Type': string
    Suburb: string
    'Unit Number': string | null
    rank: number
}

interface QldGovDataResponse {
    help: string
    success: boolean
    result: {
        resource_id: string
        fields: Array<{
            type: string
            id: string
        }>
        records: QbccInfo[]
        _links: {
            start: string
            next: string
        }
        total: number
    }
}

interface QbccPoolRegisterResponse {
    success: boolean
    message: string
    status?: 'expired' | 'valid'
    filename?: string
    presignedUrl?: string
}

interface QueryQldGovDataButtonProps {
    listingAddress: {
        streetNumber?: string
        streetName?: string
        suburb?: string
        postcode?: string
        state?: string
        manual?: boolean
        unitNumber?: string
    } | null
    onDataFound?: (data: QbccInfo) => void
}

const QueryQldGovDataButton = ({ listingAddress, onDataFound }: QueryQldGovDataButtonProps) => {
    const api = useApi()
    const listingType = useListingType()
    const { listing } = useListingContext()
    const filesApi = useListingFilesApi()

    const [loading, setLoading] = useState(false)
    const [poolData, setPoolData] = useState<QbccInfo | 'none'>()
    const [downloadingPdf, setDownloadingPdf] = useState(false)
    const [pdfStatus, setPdfStatus] = useState<{
        success: boolean
        message: string
        status?: 'expired' | 'valid'
        filename?: string
        presignedUrl?: string
    } | null>(null)

    const handleQueryQldGovData = async () => {
        if (
            !listingAddress ||
            !listingAddress.streetNumber ||
            !listingAddress.streetName ||
            !listingAddress.suburb
        ) {
            console.log('Incomplete address information')
            return
        }

        setLoading(true)
        try {
            const qldResponse: QldGovDataResponse = (await axios
                .get('https://www.data.qld.gov.au/api/3/action/datastore_search', {
                    params: {
                        resource_id: 'bb059c35-d826-4ccd-af31-24de4716864a',
                        q: `${listingAddress.streetNumber} ${listingAddress.streetName} ${listingAddress.suburb}`,
                    },
                })
                .then(prop('data'))) as QldGovDataResponse

            console.log('QLD government data for address:', qldResponse)

            if (qldResponse.success && qldResponse.result.records.length > 0) {
                const poolData = qldResponse.result.records[0]
                console.log('Pool data found:', {
                    localGovernment: poolData['Local Government Authority Area'],
                    numberOfPools: poolData['Number of Pools'],
                    sharedPool: poolData['Shared Pool Property'],
                    siteName: poolData['Site Name'],
                    streetAddress: `${poolData['Street Number']} ${poolData['Street Name']} ${poolData['Street Type']}`,
                    suburb: poolData['Suburb'],
                    unitNumber: poolData['Unit Number'],
                    postCode: poolData['Post Code'],
                })

                setPoolData(poolData)

                if (onDataFound) {
                    onDataFound(poolData)
                }
            } else {
                console.log('No pool data found for this address')
                setPoolData('none')
            }
        } catch (error) {
            console.error('Error querying QLD government data:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleDownloadPdf = async () => {
        if (!listingAddress) return

        setDownloadingPdf(true)
        setPdfStatus(null)
        try {
            const response = await api.post(`/listing/${listingType}/${listing.listingId}/poolquery`, {
                address: `${listingAddress.streetNumber} ${listingAddress.streetName} ${listingAddress.suburb}`,
            })

            const result: QbccPoolRegisterResponse = response.data
            console.log('QBCC pool register response:', result)

            if (result.success) {
                filesApi.refresh()
            }

            setPdfStatus({
                success: result.success,
                message: result.message,
                status: result.status,
                filename: result.filename,
                presignedUrl: result.presignedUrl,
            })
        } catch (error) {
            console.error('Error downloading PDF:', error)
            setPdfStatus({
                success: false,
                message: 'Failed to download certificate. Please try again.',
            })
        } finally {
            setDownloadingPdf(false)
        }
    }

    const handleViewCertificate = () => {
        if (pdfStatus?.presignedUrl) {
            window.open(pdfStatus.presignedUrl, '_blank')
        }
    }

    const isAddressComplete =
        listingAddress && listingAddress.streetNumber && listingAddress.streetName && listingAddress.suburb

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Button
                variant="outlined"
                color="primary"
                loading={loading}
                disabled={!isAddressComplete}
                onClick={handleQueryQldGovData}
                startDecorator={<Pool />}
            >
                Query QLD Pool Data
            </Button>

            <Modal
                open={!!poolData}
                onClose={() => {
                    setPoolData(undefined)
                    setPdfStatus(null)
                }}
            >
                <ModalDialog>
                    <ModalClose />
                    <DialogTitle>Pool Information</DialogTitle>
                    <DialogContent>
                        {poolData &&
                            (poolData === 'none' ? (
                                <Typography>No pool data found for this address</Typography>
                            ) : (
                                <Stack spacing={2}>
                                    <Typography>
                                        <strong>Number of Pools:</strong> {poolData['Number of Pools']}
                                    </Typography>
                                    <Typography>
                                        <strong>Shared Pool Property:</strong>{' '}
                                        {poolData['Shared Pool Property']}
                                    </Typography>
                                    <Typography>
                                        <strong>Site Name:</strong> {poolData['Site Name']}
                                    </Typography>
                                    <Typography>
                                        <strong>Street Address:</strong> {poolData['Street Number']}{' '}
                                        {poolData['Street Name']} {poolData['Street Type']}
                                    </Typography>
                                    <Typography>
                                        <strong>Suburb:</strong> {poolData['Suburb']}
                                    </Typography>
                                    {poolData['Unit Number'] && (
                                        <Typography>
                                            <strong>Unit Number:</strong> {poolData['Unit Number']}
                                        </Typography>
                                    )}
                                    {poolData['Post Code'] && (
                                        <Typography>
                                            <strong>Post Code:</strong> {poolData['Post Code']}
                                        </Typography>
                                    )}
                                    <Typography>
                                        <strong>Local Government Authority Area:</strong>{' '}
                                        {poolData['Local Government Authority Area']}
                                    </Typography>

                                    {/* PDF Download Status */}
                                    {pdfStatus && (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                p: 2,
                                                bgcolor: 'background.level1',
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Typography
                                                level="body-sm"
                                                sx={{
                                                    mb: 1,
                                                    color: pdfStatus.success ? 'success.500' : 'danger.500',
                                                }}
                                            >
                                                <strong>Certificate Status:</strong> {pdfStatus.message}
                                                {pdfStatus.presignedUrl && (
                                                    <>
                                                        <br />
                                                        <Link
                                                            href={pdfStatus.presignedUrl}
                                                            target="_blank"
                                                            sx={{ mt: 1 }}
                                                        >
                                                            View Certificate
                                                        </Link>
                                                    </>
                                                )}
                                            </Typography>

                                            {pdfStatus.status === 'expired' && (
                                                <Typography
                                                    level="body-sm"
                                                    sx={{ color: 'warning.500' }}
                                                >
                                                    ⚠️ This pool certificate has expired and cannot be
                                                    downloaded.
                                                </Typography>
                                            )}

                                            {pdfStatus.status === 'valid' && pdfStatus.presignedUrl && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                        level="body-sm"
                                                        sx={{ mb: 1 }}
                                                    >
                                                        <strong>Certificate downloaded successfully!</strong>
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        size="sm"
                                                        onClick={handleViewCertificate}
                                                        startDecorator={<Download />}
                                                    >
                                                        View Certificate
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Stack>
                            ))}
                    </DialogContent>
                    <DialogActions>
                        {poolData !== 'none' && !pdfStatus?.presignedUrl && (
                            <Button
                                variant="outlined"
                                color="primary"
                                loading={downloadingPdf}
                                disabled={!poolData || downloadingPdf}
                                onClick={handleDownloadPdf}
                                startDecorator={<Download />}
                            >
                                {pdfStatus?.status === 'expired'
                                    ? 'Certificate Expired'
                                    : 'Download Certificate'}
                            </Button>
                        )}
                        <Box>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setPoolData(undefined)
                                    setPdfStatus(null)
                                }}
                            >
                                Close
                            </Button>
                        </Box>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    )
}

export default QueryQldGovDataButton
export type { QbccInfo }
